import { useEffect, useMemo, useRef } from 'react';
import { Box, FormControl, FormHelperText, TextField, Typography } from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type UiOptions = {
  /**
   * Field name in the scaffolder formData that contains the Step Function definition JSON string.
   * Defaults to "step_function_definition_json".
   */
  definitionFieldName?: string;
};

type Props = FieldExtensionComponentProps<string[], UiOptions>;

const DEFAULT_DEFINITION_FIELD = 'step_function_definition_json';

function extractLambdaArnsFromText(text: string): string[] {
  // Capture Lambda ARNs inside JSON (placeholders like ${region} are OK).
  // Examples:
  // - arn:aws:lambda:${region}:${account_id}:function:lexid_org_wide_policy
  // - arn:aws:lambda:us-east-1:123456789012:function:my-fn:*
  // - arn:aws:lambda:${region}:${account_id}:function:migrate_ack_schedule:VERSION_TAG
  //
  // Service integrations should use the *base* function ARN (no qualifier) and add :* separately.
  const matches = Array.from(
    text.matchAll(/arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+(?::[^"'\s]+)?/g),
  ).map(m => m[0]);

  // Normalize to base ARN (strip any qualifier after the function name)
  return [
    ...new Set(
      matches.map(m =>
        m.replace(/(arn:aws:lambda:[^"'\s]+:function:[A-Za-z0-9-_]+)(?::[^"'\s]+)?$/, '$1'),
      ),
    ),
  ];
}

/**
 * Editable list of Lambda ARNs used for the Step Functions module `service_integrations.lambda.lambda`.
 *
 * Behavior:
 * - Reads the Step Function definition JSON from another field.
 * - Auto-detects Lambda ARNs in the definition and prepopulates this list.
 * - If the user manually edits the list, we stop auto-overwriting.
 */
export const LambdaIntegrationAutoDetect = (props: Props) => {
  const { onChange, schema, uiSchema, formData, formContext } = props;
  const title = schema.title || uiSchema?.['ui:title'] || 'Lambda Integration ARNs';
  const description =
    schema.description ||
    uiSchema?.['ui:description'] ||
    'Auto-detected Lambda ARNs used by the Step Function definition. You can add/remove entries.';

  const definitionFieldName =
    uiSchema?.['ui:options']?.definitionFieldName ?? DEFAULT_DEFINITION_FIELD;

  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;
  const definitionText = String(rootFormData?.[definitionFieldName] ?? '');

  const detected = useMemo(() => {
    if (!definitionText.trim()) return [];
    return extractLambdaArnsFromText(definitionText);
  }, [definitionText]);

  const value = useMemo(() => (Array.isArray(formData) ? formData : []), [formData]);

  const userTouchedRef = useRef(false);
  const prevDetectedKeyRef = useRef<string>('');

  // Auto-populate from detected values unless the user has edited manually.
  useEffect(() => {
    const detectedKey = detected.slice().sort().join('\n');
    if (detectedKey === prevDetectedKeyRef.current) return;
    prevDetectedKeyRef.current = detectedKey;

    if (userTouchedRef.current) return;

    if (value.length === 0 && detected.length > 0) {
      onChange(detected);
      return;
    }

    // If still untouched, keep it in sync (helpful when user pastes a new definition).
    if (value.length > 0 && detected.length > 0) {
      const next = [...new Set([...value, ...detected])];
      if (next.length !== value.length) {
        onChange(next);
      }
    }
  }, [detected, onChange, value]);

  return (
    <FormControl fullWidth>
      <Box mb={1}>
        <Typography variant="subtitle2">{title}</Typography>
        {description && <FormHelperText>{description}</FormHelperText>}
      </Box>

      {definitionText.trim() && detected.length === 0 && (
        <Alert severity="info" style={{ marginBottom: 8 }}>
          No Lambda ARNs detected in the definition. If you use dynamic Lambda names (or non-ARN
          values), add the ARNs manually here.
        </Alert>
      )}

      {detected.length > 0 && (
        <Alert severity="success" style={{ marginBottom: 8 }}>
          Detected {detected.length} Lambda ARN{detected.length === 1 ? '' : 's'} from the
          definition.
        </Alert>
      )}

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={value}
        onChange={(_e, newValue) => {
          userTouchedRef.current = true;
          onChange(newValue.filter(Boolean) as string[]);
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="arn:aws:lambda:${region}:${account_id}:function:my_lambda"
          />
        )}
      />

      <FormHelperText>
        Note: Terraform will add both the base ARN and the <code>:*</code> wildcard form for each
        entry.
      </FormHelperText>
    </FormControl>
  );
};

// Field extension registration moved to plugin.ts following Backstage best practices
